import * as React from 'react';
import { useState } from 'react';
import axiosInstance from '../config/axios';
import LocalStorageService from '../core/services/LocalStorageService';

/**
 * Interface for tenant object structure
 */
export interface Tenant {
  tenant_id: number;
  tenant_name: string;
}

/**
 * Interface for tenant creation response
 */
interface TenantCreateResponse {
  tenant_id: number;
  tenant_name: string;
  description: string;
}

/**
 * Interface for API error response
 */
interface ApiError {
  response?: {
    data?: {
      detail?: string;
    };
  };
}

/**
 * Props interface for TenantSelectionModal component
 */
interface TenantSelectionModalProps {
  tenants: Tenant[] | null | undefined;
  onSelect?: (tenant: Tenant) => void;
  onClose?: () => void;
}

/**
 * Modal for tenant selection and creation
 */
const TenantSelectionModal: React.FC<TenantSelectionModalProps> = ({ tenants, onSelect, onClose }) => {
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [tenantName, setTenantName] = useState<string>('');
  const [tenantDescription, setTenantDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const handleCreateTenant = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!tenantName.trim()) {
      setError('Please enter a tenant name');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const token = LocalStorageService.getAccessToken();
      if (!token) {
        setError('You must be logged in to create a tenant');
        setIsSubmitting(false);
        return;
      }

      const response = await axiosInstance.post<TenantCreateResponse>('/api/tenants/', {
        name: tenantName,
        description: tenantDescription
      });

      const newTenant = response.data;
      LocalStorageService.setSelectedTenantId(newTenant.tenant_id);

      setSuccess('Tenant created successfully! Switching to new tenant...');

      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (err) {
      const typedErr = err as ApiError;
      const detail = typedErr.response?.data?.detail || "unknown error";
      setError(`Failed to create tenant: ${detail}`);
      setIsSubmitting(false);
    }
  };

  const handleTenantNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setTenantName(e.target.value);
  };

  const handleTenantDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setTenantDescription(e.target.value);
  };

  const handleShowCreateForm = (): void => {
    setShowCreateForm(true);
    setError('');
    setSuccess('');
  };

  const handleBackToSelection = (): void => {
    setShowCreateForm(false);
    setTenantName('');
    setTenantDescription('');
    setError('');
    setSuccess('');
  };

  return (
    <div data-testid="tenant-selection-modal" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: '#fff',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '80vh',
        overflowY: 'auto',
        position: 'relative'
      }}>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              lineHeight: '1',
              padding: '5px 10px'
            }}
            aria-label="Close"
          >
            ×
          </button>
        )}

        {!showCreateForm ? (
          <>
            <h2 style={{ margin: '0 0 16px', textAlign: 'center' }}>Tenant Management</h2>

            {tenants && tenants.length > 0 && (
              <>
                <p style={{ margin: '0 0 16px', textAlign: 'center' }}>
                  {tenants.length === 1 ? 'Current Tenant:' : 'Select a Tenant:'}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                  {tenants.map((tenant: Tenant) => (
                    <button
                      key={tenant.tenant_id}
                      onClick={() => onSelect?.(tenant)}
                      style={{
                        padding: '10px',
                        border: '1px solid #ccc',
                        borderRadius: '10px',
                        backgroundColor: '#f9f9f9',
                        cursor: 'pointer',
                        fontSize: '16px'
                      }}
                    >
                      {tenant.tenant_name}
                    </button>
                  ))}
                </div>
              </>
            )}

            <div style={{
              borderTop: tenants && tenants.length > 0 ? '1px solid #ddd' : 'none',
              paddingTop: tenants && tenants.length > 0 ? '16px' : '0',
              textAlign: 'center'
            }}>
              <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#666' }}>
                Want to create a new tenant?
              </p>
              <button
                onClick={handleShowCreateForm}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #03045e',
                  borderRadius: '10px',
                  backgroundColor: '#03045e',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                Create New Tenant
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ margin: '0 0 16px', textAlign: 'center' }}>Create New Tenant</h2>

            {success && (
              <div
                style={{
                  marginBottom: '10px',
                  padding: '10px',
                  backgroundColor: '#d4edda',
                  color: '#155724',
                  borderRadius: '4px'
                }}
              >
                {success}
              </div>
            )}

            {error && (
              <div
                style={{
                  marginBottom: '10px',
                  padding: '10px',
                  backgroundColor: '#f8d7da',
                  color: '#721c24',
                  borderRadius: '4px'
                }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleCreateTenant}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Tenant Name
                </label>
                <input
                  type="text"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter tenant name"
                  value={tenantName}
                  onChange={handleTenantNameChange}
                  disabled={isSubmitting}
                  data-testid="tenant-name"
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Tenant Description (Optional)
                </label>
                <textarea
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    minHeight: '80px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Describe your tenant"
                  value={tenantDescription}
                  onChange={handleTenantDescriptionChange}
                  disabled={isSubmitting}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={handleBackToSelection}
                  style={{
                    flex: 1,
                    background: '#ccc',
                    color: '#333',
                    border: 'none',
                    padding: '10px 15px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                  disabled={isSubmitting}
                >
                  Back
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    background: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '10px 15px',
                    borderRadius: '10px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  disabled={isSubmitting}
                  data-testid="create-tenant-button"
                >
                  {isSubmitting ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default TenantSelectionModal; 
